import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, User, Sparkles, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const statusLabel = (s) => s === "confirmed" ? "Konfirmuar" : s === "cancelled" ? "Anulluar" : "Në pritje";
const statusTone = (s) => s === "confirmed" ? "bg-sage/10 text-sage border-sage/30" : s === "cancelled" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-700 border-amber-300";

export default function ParentConsultations() {
    const [teachers, setTeachers] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);  // { teacher_id, teacher_name, students[] }
    const [availability, setAvailability] = useState([]);
    const [studentId, setStudentId] = useState("");
    const [topic, setTopic] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                api.get("/parent/consultation-teachers"),
                api.get("/parent/consultation-bookings"),
            ]);
            setTeachers(r1.data);
            setBookings(r2.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const openTeacher = async (t) => {
        setSelected(t);
        setStudentId(t.students[0]?.id || "");
        setTopic("");
        try {
            const r = await api.get("/parent/consultation-availability", { params: { teacher_id: t.teacher_id } });
            setAvailability(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const book = async (slot) => {
        if (!studentId) { toast.error("Zgjidh një fëmijë"); return; }
        setSubmitting(true);
        try {
            await api.post("/parent/consultation-booking", {
                teacher_id: selected.teacher_id,
                student_id: studentId,
                date: slot.date,
                start_time: slot.start_time,
                topic,
            });
            toast.success(`Rezervuar: ${slot.date} në ${slot.start_time}`);
            setSelected(null);
            refresh();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    const cancelBooking = async (bid) => {
        if (!window.confirm("Të anulosh rezervimin?")) return;
        try {
            await api.delete(`/parent/consultation-booking/${bid}`);
            toast.success("U anulua");
            refresh();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const upcoming = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return bookings.filter((b) => b.date >= today && b.status !== "cancelled").sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
    }, [bookings]);

    const grouped = useMemo(() => {
        const g = {};
        for (const s of availability) {
            (g[s.date] = g[s.date] || []).push(s);
        }
        return g;
    }, [availability]);

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Konsultat me mësuesit</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Rezervo një takim</h1>
                <p className="text-slate-600 mt-2 text-sm">Çdo slot zgjat 30 minuta. Zgjidh mësuesin më poshtë dhe rezervo.</p>
            </div>

            {/* My upcoming bookings */}
            {upcoming.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
                    <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-sage" />
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Rezervimet e mia</div>
                        <div className="ml-auto text-xs text-slate-500">{upcoming.length}</div>
                    </div>
                    <div className="divide-y divide-slate-200">
                        {upcoming.map((b) => (
                            <div key={b.id} className="px-4 sm:px-5 py-3 flex items-center gap-3" data-testid={`my-booking-${b.id}`}>
                                <div className="w-14 text-center shrink-0">
                                    <div className="text-[10px] text-slate-500 uppercase font-bold">{b.date.slice(5)}</div>
                                    <div className="font-mono font-bold text-slate-800 text-sm">{b.start_time}</div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-900 text-sm">{b.teacher_name}</div>
                                    <div className="text-[11px] text-slate-500">{b.student_name}{b.topic && ` • ${b.topic}`}</div>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${statusTone(b.status)}`}>{statusLabel(b.status)}</span>
                                {b.status !== "cancelled" && (
                                    <button onClick={() => cancelBooking(b.id)} className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Anulo" data-testid={`cancel-my-${b.id}`}>
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Teachers list */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                    <User className="w-4 h-4 text-oxford" />
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Mësuesit në dispozicion</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-slate-500">Duke ngarkuar…</div>
                ) : teachers.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="no-teachers">Nuk ka mësues të lidhur me fëmijët tuaj</div>
                ) : (
                    <div className="divide-y divide-slate-200">
                        {teachers.map((t) => (
                            <button key={t.teacher_id} onClick={() => openTeacher(t)} disabled={!t.has_slots} className={`w-full text-left px-4 sm:px-5 py-3 flex items-center gap-3 transition ${t.has_slots ? "hover:bg-bone/40" : "opacity-50 cursor-not-allowed"}`} data-testid={`teacher-${t.teacher_id}`}>
                                <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold text-xs shrink-0">
                                    {t.teacher_name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-900 text-sm flex items-center gap-2 flex-wrap">
                                        {t.teacher_name}
                                        {t.is_kujdestari && <span className="px-1.5 py-0.5 rounded bg-terracotta/10 text-terracotta text-[9px] font-bold border border-terracotta/30">KUJDESTAR</span>}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                        {t.has_slots ? `Rezervo për: ${t.students.map((s) => s.name).join(", ")}` : "Mësuesi ende nuk ka caktuar orar"}
                                    </div>
                                </div>
                                {t.has_slots && <ChevronRight className="w-4 h-4 text-slate-400" />}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Booking modal */}
            {selected && (
                <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
                        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold text-xs">
                                {selected.teacher_name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-heading text-lg font-bold text-slate-900">{selected.teacher_name}</div>
                                <div className="text-[11px] text-slate-500">Zgjidh datën dhe orën — slote 30-min</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="p-2 rounded-md hover:bg-slate-100" data-testid="modal-close">
                                <X className="w-4 h-4 text-slate-500" />
                            </button>
                        </div>
                        <div className="px-5 py-4 border-b border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1.5">Fëmija</label>
                                <select value={studentId} onChange={(e) => setStudentId(e.target.value)} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="modal-student">
                                    {selected.students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1.5">Tema (opsionale)</label>
                                <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="p.sh., Sjellja në klasë" className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="modal-topic" />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-5 py-4">
                            {Object.keys(grouped).length === 0 ? (
                                <div className="text-center text-sm text-slate-500 py-8">Asnjë slot i disponueshëm gjatë 14 ditëve të ardhshme</div>
                            ) : (
                                Object.entries(grouped).map(([date, items]) => (
                                    <div key={date} className="mb-4">
                                        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-2 flex items-center gap-2">
                                            <Calendar className="w-3 h-3" /> {items[0].day_label} • {date}
                                        </div>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                            {items.map((s) => (
                                                <button
                                                    key={`${date}-${s.start_time}`}
                                                    disabled={!s.available || submitting}
                                                    onClick={() => book(s)}
                                                    className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                                                        !s.available
                                                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through"
                                                            : "bg-white border-slate-300 hover:border-oxford hover:bg-oxford/5 text-slate-800"
                                                    }`}
                                                    data-testid={`slot-${date}-${s.start_time.replace(":", "")}`}
                                                    title={s.location ? `Vendi: ${s.location}` : ""}
                                                >
                                                    <Clock className="inline w-3 h-3 mr-1" /> {s.start_time}
                                                    {s.location && <div className="text-[9px] text-slate-500 mt-0.5"><MapPin className="inline w-2.5 h-2.5" /> {s.location.slice(0, 14)}</div>}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="px-5 py-3 border-t border-slate-200 bg-bone/30 text-[11px] text-slate-500">
                            Zgjedhja e një slot-i e konfirmon menjëherë rezervimin
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
